import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { MRT_Column, MRT_TableInstance } from 'material-react-table';

/**
 * Google-Sheets-style "filter by values" panel, rendered inside Material React Table's
 * existing per-column filter popover via the column `Filter` render prop.
 *
 * Column filter value contract (see columnValuesFilterFn):
 *   undefined  -> no constraint (every value checked)
 *   ['a','b']  -> only rows whose cell key is in the list
 *   []         -> nothing selected, so no rows match (same as Google Sheets)
 */

// Sentinel bucket for null / undefined / '' so they collapse into one "(Blanks)" option.
const BLANK_KEY = ' __blank__';
const BLANK_LABEL = '(Blanks)';

const MAX_RENDERED = 300;
const DEBOUNCE_MS = 250;

// Same priority list formatCellValue uses when picking a display field out of an object
const MEANINGFUL_FIELDS = [
  'name', 'title', 'label', 'description', 'text', 'value',
  'firstName', 'lastName', 'fullName', 'displayName',
  'email', 'phone', 'username', 'code', 'symbol',
  'street', 'city', 'address', 'location',
];

/**
 * Stable identity for a raw cell value. Filtering compares these, never the labels, so two
 * different values that happen to render the same text can never be confused for each other.
 */
export const toFilterKey = (value: any): string => {
  if (value === null || value === undefined || value === '') return BLANK_KEY;
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/**
 * Plain-text mirror of TableViewer's formatCellValue, so the options in the list read exactly
 * like the cells do (Activated/Suspended, thousands separators, formatted dates, ...).
 * Deliberately does NOT replicate the 50-character truncation - the full text is the label and
 * the list truncates it visually instead.
 */
export const getCellText = (value: any, key?: string): string => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'object') {
    if (value.$oid) return `${String(value.$oid).substring(0, 8)}...`;

    if (value.$date) {
      const date = new Date(value.$date);
      return isNaN(date.getTime()) ? String(value.$date) : date.toLocaleDateString();
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return '';
      if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
        return `${value[0].toFixed(4)}, ${value[1].toFixed(4)}`;
      }
      if (value.length <= 3) return value.join(', ');
      return `Array (${value.length})`;
    }

    const entries = Object.entries(value);
    if (entries.length === 0) return '';

    const nonIdEntries = entries.filter(([k]) => k !== 'id' && k !== '_id' && k !== '$oid');
    if (nonIdEntries.length === 0) {
      const idEntry = entries.find(([k]) => k === 'id' || k === '_id');
      return idEntry ? `${String(idEntry[1]).substring(0, 8)}...` : '';
    }

    for (const field of MEANINGFUL_FIELDS) {
      const found = nonIdEntries.find(([k]) => k.toLowerCase().includes(field.toLowerCase()));
      if (found && found[1] && String(found[1]).trim()) return String(found[1]);
    }

    const firstMeaningful = nonIdEntries[0];
    if (firstMeaningful && firstMeaningful[1]) return String(firstMeaningful[1]);

    return `Object (${entries.length} props)`;
  }

  if (typeof value === 'boolean') {
    if (key === 'enabled') return value ? 'Activated' : 'Suspended';
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    // ID fields keep their raw digits - no thousands separators
    const isIdField = !!key && key.toLowerCase().includes('id');
    return isIdField ? value.toString() : value.toLocaleString();
  }

  if (typeof value === 'string') {
    if (value.startsWith('http://') || value.startsWith('https://') || /^(www\.)?q7q\.me/i.test(value)) {
      return value;
    }
    const dateCheck = new Date(value);
    if (!isNaN(dateCheck.getTime()) && (value.includes('-') || value.includes('T'))) {
      return `${dateCheck.toLocaleDateString()} ${dateCheck.toLocaleTimeString()}`;
    }
    return value;
  }

  return String(value);
};

// One Set per filter-value array, so a large selection stays O(1) per row instead of O(n).
const filterKeySetCache = new WeakMap<object, Set<string>>();

/**
 * Registered on the table under the `columnValues` key (see TableViewer's `filterFns` option).
 * It must be referenced by string key from the column defs - MRT overwrites inline filterFn
 * functions while preparing columns.
 */
export const columnValuesFilterFn = (row: any, columnId: string, filterValue: any): boolean => {
  if (!Array.isArray(filterValue)) return true;

  let keySet = filterKeySetCache.get(filterValue);
  if (!keySet) {
    keySet = new Set<string>(filterValue as string[]);
    filterKeySetCache.set(filterValue, keySet);
  }

  return keySet.has(toFilterKey(row.getValue(columnId)));
};

interface ValueOption {
  key: string;
  label: string;
  count: number;
}

const sortOptions = (options: ValueOption[]): ValueOption[] => {
  const blanks = options.filter((option) => option.key === BLANK_KEY);
  const rest = options.filter((option) => option.key !== BLANK_KEY);

  const allNumeric = rest.length > 0 && rest.every((option) => option.key.trim() !== '' && !isNaN(Number(option.key)));

  rest.sort(
    allNumeric
      ? (a, b) => Number(a.key) - Number(b.key)
      : (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
  );

  // Blanks always land at the bottom
  return [...rest, ...blanks];
};

const linkSx = {
  color: '#1a73e8',
  cursor: 'pointer',
  fontSize: '0.75rem',
  '&:hover': { textDecoration: 'underline' },
} as const;

interface ColumnValuesFilterProps {
  column: MRT_Column<any>;
  table: MRT_TableInstance<any>;
  /** Original data key for this column - getCellText needs it for its key-based rules */
  columnKey: string;
}

export const ColumnValuesFilter: React.FC<ColumnValuesFilterProps> = ({ column, table, columnKey }) => {
  // Core row model = every loaded row, unaffected by other columns' filters. That keeps the list
  // stable: a value you uncheck never disappears from its own list.
  const rows = table.getCoreRowModel().rows;

  const options = useMemo<ValueOption[]>(() => {
    const byKey = new Map<string, ValueOption>();

    for (const row of rows) {
      const raw = row.getValue(column.id);
      const key = toFilterKey(raw);
      const existing = byKey.get(key);

      if (existing) {
        existing.count += 1;
        continue;
      }

      byKey.set(key, {
        key,
        label: key === BLANK_KEY ? BLANK_LABEL : getCellText(raw, columnKey) || BLANK_LABEL,
        count: 1,
      });
    }

    return sortOptions(Array.from(byKey.values()));
  }, [rows, column.id, columnKey]);

  // MRT unmounts the popover contents on close, so this re-syncs from the live filter on each open
  const [selected, setSelected] = useState<Set<string> | null>(() => {
    const current = column.getFilterValue();
    return Array.isArray(current) ? new Set(current as string[]) : null;
  });
  const [search, setSearch] = useState('');

  const columnRef = useRef(column);
  columnRef.current = column;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<string[] | undefined>(undefined);
  const hasPendingRef = useRef(false);

  // Clicking outside unmounts this panel - flush any debounced change instead of dropping it
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (hasPendingRef.current) {
        hasPendingRef.current = false;
        columnRef.current.setFilterValue(pendingRef.current);
      }
    },
    []
  );

  const commit = (next: Set<string> | null) => {
    setSelected(next);

    // Everything checked normalises back to "no filter" so the funnel icon stops looking active
    const value = next === null || next.size === options.length ? undefined : Array.from(next);

    pendingRef.current = value;
    hasPendingRef.current = true;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      hasPendingRef.current = false;
      columnRef.current.setFilterValue(value);
    }, DEBOUNCE_MS);
  };

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      (option) => option.label.toLowerCase().includes(query) || option.key.toLowerCase().includes(query)
    );
  }, [options, search]);

  const toggle = (key: string) => {
    const base = selected ?? new Set(options.map((option) => option.key));
    const next = new Set(base);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    commit(next);
  };

  const isSearching = search.trim().length > 0;

  // With a search active this narrows to the matches - that is how you get the old
  // "filter this column by text" behaviour: type, then Select matching.
  const handleSelectAll = () => {
    commit(isSearching ? new Set(visible.map((option) => option.key)) : null);
  };

  const handleClear = () => commit(new Set<string>());

  const allChecked = selected === null;
  const noneChecked = selected !== null && selected.size === 0;
  const rendered = visible.slice(0, MAX_RENDERED);

  return (
    <Box sx={{ width: 280 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Box component="span" onClick={handleSelectAll} sx={linkSx}>
            {isSearching ? 'Select matching' : 'Select all'}
          </Box>
          <Box component="span" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
            -
          </Box>
          <Box component="span" onClick={handleClear} sx={linkSx}>
            Clear
          </Box>
        </Box>
        <Typography component="span" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
          Displaying {visible.length}
        </Typography>
      </Box>

      <TextField
        autoFocus
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search values..."
        size="small"
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1rem', color: '#9ca3af' }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: '8px', '& input': { fontSize: '0.8rem', py: '6px' } }}
      />

      <Box
        sx={{
          maxHeight: 260,
          overflowY: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          backgroundColor: '#ffffff',
        }}
      >
        {rendered.length === 0 ? (
          <Typography sx={{ p: '8px', fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
            No matching values
          </Typography>
        ) : (
          rendered.map((option) => {
            const checked = allChecked || !!selected?.has(option.key);
            const isBlank = option.key === BLANK_KEY;

            return (
              <Box
                key={option.key}
                role="checkbox"
                aria-checked={checked}
                tabIndex={0}
                onClick={() => toggle(option.key)}
                onKeyDown={(event) => {
                  if (event.key === ' ') {
                    event.preventDefault();
                    toggle(option.key);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  outline: 'none',
                  '&:hover': { backgroundColor: '#f3f4f6' },
                  '&:focus-visible': { backgroundColor: '#e8eaf6' },
                }}
              >
                <Checkbox
                  checked={checked}
                  readOnly
                  size="small"
                  disableRipple
                  tabIndex={-1}
                  sx={{
                    p: '4px',
                    pointerEvents: 'none',
                    color: '#3f51b5',
                    '&.Mui-checked': { color: '#3f51b5' },
                  }}
                />
                <Box
                  component="span"
                  dir="auto"
                  title={option.label}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '0.78rem',
                    color: isBlank ? '#9ca3af' : '#111827',
                    fontStyle: isBlank ? 'italic' : 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.label}
                </Box>
                <Box component="span" sx={{ fontSize: '0.68rem', color: '#9ca3af', pl: '6px', pr: '6px' }}>
                  {option.count}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {visible.length > MAX_RENDERED && (
        <Typography sx={{ mt: '6px', fontSize: '0.68rem', color: '#6b7280' }}>
          Showing first {MAX_RENDERED} of {visible.length} - refine your search
        </Typography>
      )}

      {noneChecked && (
        <Typography sx={{ mt: '6px', fontSize: '0.68rem', color: '#b45309' }}>
          No values selected - no rows will match.
        </Typography>
      )}
    </Box>
  );
};

export default ColumnValuesFilter;
